import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Scale, Snowflake, Waypoints } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v, d = 3) => (v === null || v === undefined ? "—" : typeof v === "number" ? v.toFixed(d) : String(v));
const when = (iso) => (iso ? new Date(iso).toLocaleString() : "never");

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] px-4 py-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--qn-foreground-subtle)]">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Panel({ title, children, testid, right }) {
  return (
    <section className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

function Sparkline({ values, testid }) {
  const w = 96, h = 22;
  if (!values || values.length < 2) return <span className="text-[var(--qn-foreground-subtle)] text-xs" data-testid={testid}>—</span>;
  const max = Math.max(...values, 1);
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - (v / max) * (h - 2) - 1}`).join(" ");
  return (
    <svg width={w} height={h} className="text-[var(--qn-primary)]" data-testid={testid} aria-label={`demand over last ${values.length} snapshots`}>
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx={w} cy={h - (values[values.length - 1] / max) * (h - 2) - 1} r="2" fill="currentColor" />
    </svg>
  );
}

function CellsTable({ cells, history }) {
  if (!cells?.length) {
    return <p className="text-sm text-[var(--qn-foreground-muted)]" data-testid="ri-cells-empty">No measured traffic in the window — the optimizer falls back to a synthetic default cell (recorded as such).</p>;
  }
  const series = (id) => (history || []).map((h) => h.demand?.[id] ?? 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="ri-cells-table">
        <thead className="text-[11px] uppercase tracking-wider text-[var(--qn-foreground-subtle)]">
          <tr className="text-left"><th className="py-1.5 pr-3">Cell</th><th className="pr-3">Demand</th><th className="pr-3">Last {Math.max(history?.length || 0, 0)} snapshots</th><th className="pr-3">Mean tokens</th><th className="pr-3">Chain</th><th>Realized (ok / err / timeout)</th></tr>
        </thead>
        <tbody>
          {cells.map((c) => (
            <tr key={c.cell_id} className="border-t border-[var(--qn-border)]" data-testid={`ri-cell-${c.cell_id}`}>
              <td className="py-1.5 pr-3 font-mono text-xs">{c.cell_id}</td>
              <td className="pr-3 tabular-nums">{c.demand}</td>
              <td className="pr-3"><Sparkline values={series(c.cell_id)} testid={`ri-spark-${c.cell_id}`} /></td>
              <td className="pr-3 tabular-nums">{c.tokens}</td>
              <td className="pr-3 text-xs">{c.chain_id ? `${c.chain_id} · step ${c.chain_step}` : "—"}</td>
              <td className="tabular-nums text-xs">{c.realized ? `${c.realized.success} / ${c.realized.error} / ${c.realized.timeout} (joined ${c.realized.joined})` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PairsList({ pairs }) {
  if (!pairs?.length) return <p className="text-sm text-[var(--qn-foreground-muted)]" data-testid="ri-pairs-empty">No cell pair shared a prompt fingerprint — no cache-reuse coupling is invented.</p>;
  return (
    <ul className="space-y-1.5 text-sm" data-testid="ri-pairs-list">
      {pairs.map((p) => (
        <li key={`${p.a}|${p.b}`} className="flex items-center gap-2 font-mono text-xs">
          <span>{p.a}</span><span className="text-[var(--qn-foreground-subtle)]">↔</span><span>{p.b}</span>
          <span className="ml-auto tabular-nums">score {fmt(p.score)} · {p.shared_fingerprints} shared</span>
        </li>
      ))}
    </ul>
  );
}

function Diagnostics({ d }) {
  if (!d) return <p className="text-sm text-[var(--qn-foreground-muted)]" data-testid="ri-diag-empty">No policy optimization has run yet.</p>;
  return (
    <dl className="grid grid-cols-2 gap-y-1.5 text-sm" data-testid="ri-diagnostics">
      <dt className="text-[var(--qn-foreground-muted)]">Instance source</dt><dd data-testid="ri-diag-source">{d.instance_source}</dd>
      <dt className="text-[var(--qn-foreground-muted)]">Variables</dt><dd className="tabular-nums">{d.n_vars}</dd>
      <dt className="text-[var(--qn-foreground-muted)]">Density</dt><dd className="tabular-nums">{fmt(d.density)}</dd>
      <dt className="text-[var(--qn-foreground-muted)]">Neg-eig fraction</dt><dd className="tabular-nums">{fmt(d.neg_eig_fraction)} (≥ {fmt(d.neg_eig_threshold, 2)})</dd>
      <dt className="text-[var(--qn-foreground-muted)]">Quad/lin ratio</dt><dd className="tabular-nums">{fmt(d.quad_lin_ratio)}</dd>
      <dt className="text-[var(--qn-foreground-muted)]">Enters quantum ladder</dt><dd data-testid="ri-diag-ladder">{d.enters_quantum_ladder ? "yes" : "no"}</dd>
      <dt className="text-[var(--qn-foreground-muted)]">Computed</dt><dd className="text-xs">{when(d.computed_at)}</dd>
    </dl>
  );
}

function Realized({ r }) {
  if (!r) return <p className="text-sm text-[var(--qn-foreground-muted)]" data-testid="ri-realized-empty">No predicted-vs-realized join has run yet.</p>;
  const pv = r.predicted_vs_realized_routed_cost || {};
  const sv = r.realized_savings || {};
  return (
    <dl className="grid grid-cols-2 gap-y-1.5 text-sm" data-testid="ri-realized">
      <dt className="text-[var(--qn-foreground-muted)]">Classification</dt><dd data-testid="ri-realized-class">{r.classification}</dd>
      <dt className="text-[var(--qn-foreground-muted)]">Priced pairs / excluded</dt><dd className="tabular-nums">{r.n_priced} / {r.unpriced_excluded} (of {r.n_audit} logged, {r.days}d)</dd>
      <dt className="text-[var(--qn-foreground-muted)]">Realized savings</dt><dd className="tabular-nums" data-testid="ri-realized-savings">{sv.savings_pct === null || sv.savings_pct === undefined ? "—" : `${sv.savings_pct}%`} (${fmt(sv.routed_usd, 4)} vs ${fmt(sv.baseline_usd, 4)})</dd>
      <dt className="text-[var(--qn-foreground-muted)]">Cost prediction MAPE</dt><dd className="tabular-nums">{fmt(pv.mape)} · within 10%: {fmt(pv.within_10pct, 2)}</dd>
      <dt className="text-[var(--qn-foreground-muted)]">Token estimate MAPE</dt><dd className="tabular-nums">{fmt(r.token_estimate_accuracy?.mape)}</dd>
      <dt className="text-[var(--qn-foreground-muted)]">Shadow joined</dt><dd className="tabular-nums">{r.n_shadow_joined} · p95 coverage {fmt(r.shadow_layer1?.latency_p95_coverage, 2)}</dd>
      <dd className="col-span-2 text-xs text-[var(--qn-foreground-subtle)]">{r.note}</dd>
    </dl>
  );
}

function QueueStatus({ q }) {
  if (!q) return null;
  const label = q.stage || (q.ready ? "ready" : "not started");
  return (
    <div className="rounded-lg border border-[var(--qn-border)] px-3 py-2 text-xs flex flex-wrap gap-x-4 gap-y-1" data-testid="ri-queue">
      <span className="font-semibold">R4b checkpoint queue: <span data-testid="ri-queue-stage">{label}</span></span>
      <span>Q1 batch {q.q1?.finished ? "finished" : q.q1?.started ? `running · ${q.q1?.cells_done} cells` : "not started"}</span>
      <span>dev bundles {q.dev_bundles}/{q.dev_bundles_required}</span>
      <span>runner {q.runner_alive ? "alive" : "not running"}</span>
      {q.error && <span className="text-red-400">{q.error}</span>}
    </div>
  );
}

function Bundles({ r4b }) {
  const bundles = r4b?.bundles || [];
  return (
    <div className="space-y-2 text-sm">
      <QueueStatus q={r4b?.queue} />
      <p className="text-xs text-[var(--qn-foreground-subtle)] font-mono break-all" data-testid="ri-r4b-digest">frozen {r4b?.frozen_digest} · held-out {r4b?.held_out_digest}</p>
      {!bundles.length && <p className="text-[var(--qn-foreground-muted)]" data-testid="ri-bundles-empty">No frozen bundle yet. Freezing needs ≥ {r4b?.frozen?.min_cells} measured cells and fresh prices; nothing is padded.</p>}
      {bundles.map((b) => (
        <div key={b.bundle_id} className="rounded-lg border border-[var(--qn-border)] px-3 py-2 flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid={`ri-bundle-${b.bundle_id}`}>
          <span className="font-semibold">{b.bundle_id}</span><span>{b.phase}</span>
          <span>{b.n_cells} cells · {b.n_pairs} pairs · {b.n_vars} vars</span>
          <span>{b.admission || "—"}</span>
          <span className="text-[var(--qn-foreground-subtle)]">{when(b.frozen_at)}</span>
        </div>
      ))}
    </div>
  );
}

export default function RoutingInteractionsPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/platform/routing-interactions`)).data); setErr(null); }
    catch (e) { setErr(e?.response?.status === 403 ? "Platform admin only." : "Could not load routing interactions."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (path, okMsg) => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/platform/routing-interactions${path}`);
      toast.success(typeof okMsg === "function" ? okMsg(r) : okMsg);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  if (err) return <div className="px-8 text-sm text-[var(--qn-foreground-muted)]" data-testid="ri-error">{err}</div>;
  if (!data) return <div className="px-8 text-sm text-[var(--qn-foreground-muted)]" data-testid="ri-loading">Loading…</div>;
  const s = data.snapshot;
  const agg = data.aggregator || {};
  return (
    <div className="px-8 pb-10 space-y-5" data-testid="routing-interactions-panel">
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1.5 text-xs text-[var(--qn-foreground-muted)]"><Waypoints className="w-3.5 h-3.5" />
          Aggregator {agg.running ? "running" : "stopped"} · every {agg.interval_s}s · {agg.runs} runs · last {when(agg.last_run_at)}</span>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => act("/aggregate", (r) => `Snapshot ${r.status}: ${r.cells.length} cells`)} className="ml-auto gap-1.5" data-testid="ri-aggregate-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Aggregate now
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => act("/realized/join", (r) => `Realized join: ${r.classification} (${r.n_priced} priced pairs)`)} className="gap-1.5" data-testid="ri-realized-btn">
          <Scale className="w-3.5 h-3.5" /> Join realized
        </Button>
        <Button size="sm" disabled={busy} onClick={() => act("/r4b/freeze", (r) => `R4b freeze: ${r.status}${r.bundle_id ? ` (${r.bundle_id})` : ""}${r.price_refresh ? ` · prices ${r.price_refresh.status}` : ""}`)} className="gap-1.5" data-testid="ri-freeze-btn">
          <Snowflake className="w-3.5 h-3.5" /> Freeze R4b dev bundle
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Snapshot" value={s ? s.status : "none"} testid="ri-stat-status" />
        <Stat label="Window" value={s ? `${Math.round(s.window_s / 60)} min` : "—"} testid="ri-stat-window" />
        <Stat label="Requests" value={s ? s.n_shadow_decisions : "—"} testid="ri-stat-requests" />
        <Stat label="Cells / pairs" value={s ? `${s.cells.length} / ${s.cooccurrence.length}` : "—"} testid="ri-stat-cells" />
        <Stat label="Incident feed" value={s ? s.incident_rates_status : "—"} testid="ri-stat-incidents" />
      </div>
      <Panel title="Traffic cells (measured demand buckets)" testid="ri-cells-panel"
        right={<span className="text-xs text-[var(--qn-foreground-subtle)]">{s ? `computed ${when(s.computed_at)}` : ""}</span>}>
        <CellsTable cells={s?.cells} history={data.history} />
      </Panel>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <Panel title="Co-occurrence (shared prompt fingerprints)" testid="ri-pairs-panel"><PairsList pairs={s?.cooccurrence} /></Panel>
        <Panel title="Latest R4 policy diagnostics" testid="ri-diag-panel"><Diagnostics d={data.latest_policy_diagnostics} /></Panel>
      </div>
      <Panel title="Realized outcomes (C9/M7 predicted-vs-realized join)" testid="ri-realized-panel"><Realized r={data.realized} /></Panel>
      <Panel title="R4b bench bundles (frozen, immutable)" testid="ri-bundles-panel"><Bundles r4b={data.r4b} /></Panel>
    </div>
  );
}
